import React from 'react';
import { Button, Container, Row, Col } from "react-bootstrap";
import { 
  ArrowRight, 
  Star, 
  Zap, 
  Target,
  Award,
  Users,
  BarChart2,
  CheckCircle,
  PlayCircle
} from "react-feather";

const styles = {
  hero: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '100px 0',
    position: 'relative',
    overflow: 'hidden',
  },
  particle: {
    position: 'absolute',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    animation: 'float 20s infinite linear',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    marginBottom: '1.5rem',
    background: 'linear-gradient(45deg, #fff, #f0f0f0)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  subtitle: {
    fontSize: '1.25rem',
    opacity: '0.9',
    marginBottom: '2rem',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px',
    marginTop: '60px',
  },
  featureItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '20px',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
  },
  featureItemHover: {
    transform: 'translateY(-5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  },
  featureIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 15px',
  },
  featureTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '5px',
  },
  ctaButton: {
    background: 'linear-gradient(45deg, #10b981, #3b82f6)',
    border: 'none',
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderRadius: '50px',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  },
  ctaButtonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
  },
  secondaryButton: {
    background: 'transparent',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderRadius: '50px',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  },
  secondaryButtonHover: {
    borderColor: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  statsContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '20px',
    padding: '40px',
    marginTop: '80px',
    backdropFilter: 'blur(10px)',
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: '#fff',
  },
  statLabel: {
    fontSize: '0.9rem',
    opacity: '0.8',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  badge: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
  },
  videoButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '12px 25px',
    borderRadius: '50px',
    textDecoration: 'none',
    color: 'white',
    transition: 'all 0.3s ease',
  },
  videoButtonHover: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
  },
};

function Hero() {
  const [hoverStates, setHoverStates] = React.useState({});

  const features = [
    { icon: <Zap size={24} />, title: "Fast Delivery", color: "#f59e0b" },
    { icon: <Target size={24} />, title: "Targeted", color: "#3b82f6" },
    { icon: <Star size={24} />, title: "Premium", color: "#8b5cf6" },
    { icon: <Award size={24} />, title: "Award Winning", color: "#10b981" },
    { icon: <Users size={24} />, title: "Team Experts", color: "#ef4444" },
    { icon: <BarChart2 size={24} />, title: "Data Driven", color: "#ec4899" },
  ];

  return (
    <section className="hero text-white" style={styles.hero}>
      {/* Particles Background */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            ...styles.particle,
            width: Math.random() * 100 + 50,
            height: Math.random() * 100 + 50,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
          }}
        />
      ))}

      <Container>
        {/* Badge */}
        <div style={styles.badge}>
          <CheckCircle size={16} />
          Trusted by 221+ Companies
        </div>

        {/* Main Title */}
        <h1 style={styles.title}>
          Transform Your Business
          <br />
          with <span style={{ color: '#10b981' }}>VIXO</span> Solutions
        </h1>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          We provide comprehensive digital solutions including Websites, 
          Marketing Strategies, Targeted Ads, and Premium Content creation 
          to accelerate your business growth.
        </p>

        {/* CTA Buttons */}
        <div className="d-flex flex-column flex-md-row gap-3 justify-content-center align-items-center">
          <Button
            variant="primary"
            style={{
              ...styles.ctaButton,
              ...(hoverStates.primary ? styles.ctaButtonHover : {})
            }}
            onMouseEnter={() => setHoverStates({...hoverStates, primary: true})}
            onMouseLeave={() => setHoverStates({...hoverStates, primary: false})}
          >
            Start Free Trial <ArrowRight size={18} />
          </Button>

          <a
            href="#demo"
            style={{
              ...styles.videoButton,
              ...(hoverStates.video ? styles.videoButtonHover : {})
            }}
            onMouseEnter={() => setHoverStates({...hoverStates, video: true})}
            onMouseLeave={() => setHoverStates({...hoverStates, video: false})}
          >
            <PlayCircle size={18} />
            Watch Demo
          </a>
        </div>

        {/* Features Grid */}
        <div style={styles.featureGrid}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                ...styles.featureItem,
                ...(hoverStates[`feature-${index}`] ? styles.featureItemHover : {})
              }}
              onMouseEnter={() => setHoverStates({...hoverStates, [`feature-${index}`]: true})}
              onMouseLeave={() => setHoverStates({...hoverStates, [`feature-${index}`]: false})}
            >
              <div style={{...styles.featureIcon, backgroundColor: `${feature.color}20`}}>
                <div style={{ color: feature.color }}>
                  {feature.icon}
                </div>
              </div>
              <div style={styles.featureTitle}>{feature.title}</div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={styles.statsContainer}>
          <Row className="text-center">
            <Col md={3} sm={6}>
              <div style={styles.statNumber}>98%</div>
              <div style={styles.statLabel}>Client Satisfaction</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={styles.statNumber}>223+</div>
              <div style={styles.statLabel}>Projects Delivered</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={styles.statNumber}>24/7</div>
              <div style={styles.statLabel}>Support Available</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={styles.statNumber}>50+</div>
              <div style={styles.statLabel}>Countries Served</div>
            </Col>
          </Row>
        </div>

        {/* Trusted By */}
        <div className="text-center mt-5">
          <p style={{ opacity: '0.7', marginBottom: '20px' }}>
            Trusted by industry leaders
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-5">
            {['TechCorp', 'GlobalBiz', 'StartupX', 'EnterprisePro', 'InnovateCo'].map((company, i) => (
              <div key={i} style={{ opacity: '0.5', fontSize: '1.2rem', fontWeight: '600' }}>
                {company}
              </div>
            ))}
          </div>
        </div>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
            }
          }
          
          .hero {
            position: relative;
            overflow: hidden;
          }
          
          @media (max-width: 768px) {
            h1 {
              font-size: 2.5rem !important;
            }
            
            .feature-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .stats-container {
              padding: 20px !important;
            }
          }
        `}
      </style>
    </section>
  );
}

export default Hero;